import { omit } from 'lodash';
import useSWR, { mutate } from 'swr';
import { useState, useEffect } from 'react';

import { fetcher, endpoints, postFetcher } from 'src/lib/axios';

import { toast } from 'src/components/snackbar';

const jobSwrOptions = { dedupingInterval: 10_000 } as const;

export function useJobPostingDetails(jobId: string | null | undefined) {
  const key = jobId ? endpoints.jobs.details(jobId) : null;
  return useSWR(key, fetcher, jobSwrOptions);
}

export function useJobWorkflowStages(jobId: string | null | undefined) {
  const key = jobId ? endpoints.jobs.workflowStages(jobId) : null;
  return useSWR(key, fetcher, jobSwrOptions);
}

/**
 * Per-stage candidate counts under `appliedFilters` — pass the same payload the candidate list posts,
 * minus its stage selection, so every tab's count matches what that tab will list. Its sort is dropped,
 * since reordering can't change a count.
 */
export function useJobWorkflowStageCounts(
  jobId: string | null | undefined,
  appliedFilters: Record<string, unknown>,
  isHydrating?: boolean
) {
  const countPayload = JSON.stringify(omit(appliedFilters, 'SortByAiScore'));
  const key: [string, string] | null =
    jobId && !isHydrating ? [endpoints.jobs.stageCounts(jobId), countPayload] : null;

  return useSWR(key, postFetcher, jobSwrOptions);
}

export function revalidateJobWorkflowStageCounts(jobId: string) {
  const url = endpoints.jobs.stageCounts(jobId);
  return mutate((key) => Array.isArray(key) && key[0] === url);
}

export function useJobWorkflowDetails(jobId: string | null | undefined) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!jobId) {
      setReady(false);
      return undefined;
    }

    const idleCallback = window.requestIdleCallback || ((fn: () => void) => setTimeout(fn, 200));
    const idleId = idleCallback(() => setReady(true), { timeout: 5000 });

    return () => {
      if ('cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      } else {
        clearTimeout(idleId as unknown as number);
      }
    };
  }, [jobId]);

  const key = ready && jobId ? endpoints.job_workflows.get(jobId) : null;

  return useSWR(key, fetcher, {
    ...jobSwrOptions,
    revalidateOnFocus: false,
    onError: () => toast.error('Failed', { description: 'Error fetching workflow details' }),
  });
}
